import { useState, useEffect, useRef } from 'react';
import { Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';

interface OnlineMember {
  id: string;
  display_name?: string;
  email?: string;
  avatar_url?: string;
  last_seen?: string;
}

// Time in milliseconds to consider a user as offline (5 minutes)
const OFFLINE_THRESHOLD = 5 * 60 * 1000;

// Special user ID that should always be shown first and online
const SPECIAL_USER_ID = "2327369a-b920-4a20-90ec-4805b05edc93";

type OnlineMembersVariant = 'fixed' | 'inline';

export function OnlineMembers({ variant = 'fixed' }: { variant?: OnlineMembersVariant }) {
  const [onlineMembers, setOnlineMembers] = useState<OnlineMember[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [additionalUserCount, setAdditionalUserCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Get color based on index - similar to RecentUsersStats
  const getAvatarColor = (index: number) => {
    const colors = [
      'from-pink-500 to-rose-500',
      'from-blue-500 to-indigo-500',
      'from-green-500 to-emerald-500',
      'from-amber-500 to-orange-500',
      'from-purple-500 to-violet-500',
      'from-cyan-500 to-sky-500',
      'from-red-500 to-pink-500',
      'from-teal-500 to-cyan-500',
    ];
    return colors[index % colors.length];
  };

  // Generate a random number between 6-10 for additional users
  useEffect(() => {
    const randomAdditionalUsers = Math.floor(Math.random() * 5) + 6; // Random number between 6-10
    setAdditionalUserCount(randomAdditionalUsers);
  }, []);

  useEffect(() => {
    // Initialize the component by getting the current user
    const initComponent = async () => {
      try {
        // We'll handle special user fetching in fetchOnlineUsers
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        setCurrentUser(user);
        
        // Update user's online status immediately
        await updateUserStatus(user.id);
        
        // Set up ping interval to keep the user online (every 30 seconds)
        pingIntervalRef.current = setInterval(() => {
          updateUserStatus(user.id);
        }, 30 * 1000);
        
        // Set up cleanup interval to remove offline users (every minute)
        cleanupIntervalRef.current = setInterval(() => {
          cleanupOfflineUsers();
        }, 60 * 1000);
        
        // Fetch initial list of online users
        fetchOnlineUsers();
        
        // Subscribe to changes in the online_status table
        const channel = supabase
          .channel('online-users-changes')
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'online_status'
          }, () => {
            console.log('Online status changed');
            fetchOnlineUsers();
          })
          .subscribe();
          
        return () => {
          channel.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing component:', error);
      }
    };
    
    initComponent();
    
    // Clean up on unmount
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
      if (currentUser) {
        // Set user as offline when leaving
        markUserOffline(currentUser.id);
      }
    };
  }, []); // Empty dependency array means this runs once on mount
  
  // Update the user's online status
  const updateUserStatus = async (userId: string) => {
    try {
      const now = new Date().toISOString();
      
      // Upsert into online_status table
      const { error } = await supabase
        .from('online_status')
        .upsert({
          user_id: userId,
          last_seen: now,
          is_online: true
        }, {
          onConflict: 'user_id'
        });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating online status:', error);
      
      // If table doesn't exist, create it
      if (typeof error === 'object' && error && 'code' in error && error.code === '42P01') {
        await createOnlineStatusTable();
        // Try again after creating the table
        await updateUserStatus(userId);
      }
    }
  };
  
  // Mark a user as offline
  const markUserOffline = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('online_status')
        .update({ is_online: false })
        .eq('user_id', userId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error marking user offline:', error);
    }
  };
  
  // Clean up users who haven't been seen recently
  const cleanupOfflineUsers = async () => {
    try {
      const cutoffTime = new Date(Date.now() - OFFLINE_THRESHOLD).toISOString();
      
      const { error } = await supabase
        .from('online_status')
        .update({ is_online: false })
        .lt('last_seen', cutoffTime)
        .eq('is_online', true);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error cleaning up offline users:', error);
    }
  };
  
  // Create the online_status table if it doesn't exist
  const createOnlineStatusTable = async () => {
    try {
      // We'll use RPC to create the table if it doesn't exist
      // This requires a SQL function to be created in Supabase
      // For now, we'll attempt to upsert and catch the error
      console.log('Attempted to create online_status table');
    } catch (error) {
      console.error('Error creating online_status table:', error);
    }
  };
  
  // Fetch users who are currently online
  const fetchOnlineUsers = async () => {
    try {
      // Always fetch the special user first
      const { data: specialUserData } = await supabase
        .from('profiles')
        .select('id, display_name, email, avatar_url')
        .eq('id', SPECIAL_USER_ID)
        .single();
      
      // Store special user data for later use
      let specialUser: OnlineMember | null = null;
      
      if (specialUserData) {
        specialUser = specialUserData;
      } else {
        // If special user doesn't exist, create a placeholder
        specialUser = {
          id: SPECIAL_USER_ID,
          display_name: "Marko Filipovic",
          avatar_url: "https://api.dicebear.com/7.x/avataaars/svg?seed=Marko"
        };
      }
      
      // Get online users from the online_status table
      const now = new Date();
      const cutoffTime = new Date(now.getTime() - OFFLINE_THRESHOLD);
      const cutoffTimeISO = cutoffTime.toISOString();
      
      const { data: onlineStatus, error: statusError } = await supabase
        .from('online_status')
        .select('user_id, last_seen')
        .gte('last_seen', cutoffTimeISO);
        
      if (statusError) throw statusError;
      
      if (!onlineStatus || onlineStatus.length === 0) {
        // If no online users, just show the special user
        if (specialUser) {
          setOnlineMembers([specialUser]);
        } else {
          setOnlineMembers([]);
        }
        return;
      }
      
      // Get the user IDs of online users
      const onlineUserIds = onlineStatus.map((status: { user_id: string }) => status.user_id);
      
      // Fetch profiles for online users
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, email, avatar_url')
        .in('id', onlineUserIds)
        .neq('id', SPECIAL_USER_ID); // Exclude special user as we'll add it manually
      
      // Create final members list with special user first
      const allMembers = [];
      if (specialUser) {
        allMembers.push(specialUser);
      }
      
      if (profiles && profiles.length > 0) {
        allMembers.push(...profiles);
      }
      
      setOnlineMembers(allMembers);
    } catch (error) {
      console.error('Error fetching online users:', error);
      
      // If there's an error, at least show the special user
      const baseMembers = [];
      
      // Try to get the special user
      try {
        const { data: specialUserData } = await supabase
          .from('profiles')
          .select('id, display_name, email, avatar_url')
          .eq('id', SPECIAL_USER_ID)
          .single();
            
        if (specialUserData) {
          baseMembers.push(specialUserData);
        } else {
          // Use a placeholder for the special user
          baseMembers.push({
            id: SPECIAL_USER_ID,
            display_name: "Marko Filipovic",
            avatar_url: "https://api.dicebear.com/7.x/avataaars/svg?seed=Marko"
          });
        }
      } catch (error) {
        console.error('Error fetching special user:', error);
      }
      
      // Also try to show the current user if different from special user
      if (currentUser && currentUser.id !== SPECIAL_USER_ID) {
        try {
          const { data: currentUserProfile } = await supabase
            .from('profiles')
            .select('id, display_name, email, avatar_url')
            .eq('id', currentUser.id)
            .single();
            
          if (currentUserProfile) {
            baseMembers.push(currentUserProfile);
          }
        } catch (error) {
          console.error('Error fetching current user profile:', error);
        }
      }
      
      setOnlineMembers(baseMembers);
    }
  };
  
  // If rendering fixed widget, hide it on mobile since we'll show inline version there
  if (variant === 'fixed' && isMobile) {
    // Still keep modal capability if it was opened somehow
    return (
      <>
        {expanded && (
          <motion.div 
            className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpanded(false)}
          >
            <motion.div 
              className="rounded-xl border border-[#c9fffc]/20 w-full max-w-sm mx-4 overflow-hidden shadow-xl bg-[#151D29]/90 backdrop-blur-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-[#c9fffc]/10 flex items-center justify-between">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <div className="relative">
                    <Users className="w-4 h-4 text-[#c9fffc]" />
                    <span className="absolute -top-1 -right-1 bg-green-500 w-1.5 h-1.5 rounded-full"></span>
                  </div>
                  {onlineMembers.length + additionalUserCount}
                </h3>
                <button 
                  className="text-gray-400 hover:text-white"
                  onClick={() => setExpanded(false)}
                >
                  ×
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {onlineMembers.length > 0 ? (
                  <div className="py-1">
                    {onlineMembers.map((member, index) => (
                      <div key={member.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#c9fffc]/5">
                        <div className="relative">
                          <div 
                        className={`w-10 h-10 rounded-full ring-2 ring-gray-900 overflow-hidden flex-shrink-0 flex items-center justify-center text-white text-xs font-medium shadow-lg bg-gradient-to-br ${getAvatarColor(index % 8)}`}
                    >
                            {member.avatar_url ? (
                              <img 
                                src={member.avatar_url} 
                                alt={member.display_name || member.email || 'User'} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              member.display_name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || '?'
                            )}
                          </div>
                          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border border-[#151D29]"></div>
                        </div>
                        <div className="truncate">
                          <div className="text-sm text-white font-medium truncate">
                            {member.display_name || member.email || 'Anonymous User'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-center text-gray-400 text-sm">
                    No users online
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </>
    );
  }

  return (
    <div className="relative z-30">
      {variant === 'fixed' ? (
        <div className="fixed rounded-lg overflow-hidden"
          style={{
            top: isMobile ? '10px' : '5rem',
            left: isMobile ? 'auto' : 'auto',
            right: isMobile ? '1rem' : '1.5rem',
            zIndex: 40
          }}
        >
          <div 
            className="flex items-center p-3 cursor-pointer"
            onClick={() => setExpanded(true)}
          >
            {/* Stats section similar to RecentUsersStats */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <div className="flex -space-x-3">
                  {onlineMembers.slice(0, 5).map((member, index) => (
                    <div 
                      key={member.id} 
                      className="relative group"
                    >
                      <div 
                        className={`w-8 h-8 rounded-full ring-2 ring-gray-900 bg-gradient-to-br ${getAvatarColor(index)} flex items-center justify-center text-white text-xs font-medium shadow-lg transition-transform duration-300  overflow-hidden`}
                      >
                        {member.avatar_url ? (
                          <img 
                            src={member.avatar_url} 
                            alt={member.display_name || member.email || 'User'} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          member.display_name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || '?'
                        )}
                        
                        {/* Green online indicator */}
                        <div 
                          className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-[#151D29]"
                          style={{ zIndex: 10 }}
                        ></div>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 whitespace-nowrap">
                        {member.display_name || member.email || 'Anonymous User'}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="pl-2 flex items-center">
                  <Users className="w-4 h-4 text-[#c9fffc] mr-1" />
                  <span className="text-white text-sm font-medium">{onlineMembers.length + additionalUserCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Inline compact version for mobile category row
        <div 
          className="inline-flex items-center gap-2 bg-[#151D29]/80 backdrop-blur-md border border-[#c9fffc]/20 rounded-lg py-2 px-3 text-white"
          onClick={() => setExpanded(true)}
        >
          <div className="flex -space-x-2">
            {onlineMembers.slice(0, 3).map((member, index) => (
              <div key={member.id} className="relative">
                <div className={`w-6 h-6 rounded-full ring-2 ring-gray-900 bg-gradient-to-br ${getAvatarColor(index)} overflow-hidden`}> 
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.display_name || member.email || 'User'} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] text-white flex items-center justify-center w-full h-full">
                      {member.display_name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-green-500 rounded-full border border-[#151D29]"></div>
              </div>
            ))}
          </div>
          <div className="pl-1 flex items-center">
            <Users className="w-4 h-4 text-[#c9fffc] mr-1" />
            <span className="text-sm font-medium">{onlineMembers.length + additionalUserCount}</span>
          </div>
        </div>
      )}
      
      {/* Modal dialog when expanded */}
      {expanded && (
        <motion.div 
          className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setExpanded(false)}
        >
          <motion.div 
            className="rounded-xl border border-[#c9fffc]/20 w-full max-w-sm mx-4 overflow-hidden shadow-xl bg-[#151D29]/90 backdrop-blur-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#c9fffc]/10 flex items-center justify-between">
              <h3 className="text-white font-medium flex items-center gap-2">
                <div className="relative">
                  <Users className="w-4 h-4 text-[#c9fffc]" />
                  <span className="absolute -top-1 -right-1 bg-green-500 w-1.5 h-1.5 rounded-full"></span>
                </div>
                {onlineMembers.length + additionalUserCount}
              </h3>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setExpanded(false)}
              >
                ×
              </button>
            </div>
            
            <div className="max-h-[60vh] overflow-y-auto">
              {onlineMembers.length > 0 ? (
                <div className="py-1">
                  {onlineMembers.map((member, index) => (
                    <div key={member.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#c9fffc]/5">
                      <div className="relative">
                        <div 
                      className={`w-10 h-10 rounded-full ring-2 ring-gray-900 overflow-hidden flex-shrink-0 flex items-center justify-center text-white text-xs font-medium shadow-lg bg-gradient-to-br ${getAvatarColor(index % 8)}`}
                    >
                          {member.avatar_url ? (
                            <img 
                              src={member.avatar_url} 
                              alt={member.display_name || member.email || 'User'} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            member.display_name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || '?'
                          )}
                        </div>
                        <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border border-[#151D29]"></div>
                      </div>
                      <div className="truncate">
                        <div className="text-sm text-white font-medium truncate">
                          {member.display_name || member.email || 'Anonymous User'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-gray-400 text-sm">
                  No users online
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {variant === 'fixed' && (
        // Clickable area to open the expanded view
        <div 
          className="fixed w-48 h-20 z-30 cursor-pointer"
          onClick={() => setExpanded(true)}
          style={{
            top: isMobile ? '10px' : '5rem',
            left: isMobile ? 'auto' : 'auto',
            right: isMobile ? '1rem' : '1.5rem',
          }}
          aria-label="View online users"
        ></div>
      )}
    </div>
  );
}
